import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import AIGeneratedBadge from '../components/AIGeneratedBadge';
import { 
  ArrowLeft, Search, Filter, Star, StarOff, ExternalLink, 
  RefreshCw, Telescope, Calendar, Tag, Globe, Heart, Bell, BellOff
} from 'lucide-react';
import { toast } from 'sonner';
import { motion, AnimatePresence } from 'framer-motion';

const TAG_COLORS = {
  'investissement': 'bg-green-500/20 text-green-400 border-green-500/30',
  'fiscalité': 'bg-orange-500/20 text-orange-400 border-orange-500/30',
  'marché locatif': 'bg-blue-500/20 text-blue-400 border-blue-500/30',
  'législation': 'bg-purple-500/20 text-purple-400 border-purple-500/30',
  'crédit': 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30',
  'construction': 'bg-red-500/20 text-red-400 border-red-500/30',
  'marché': 'bg-cyan-500/20 text-cyan-400 border-cyan-500/30',
  'actualité': 'bg-gray-500/20 text-gray-400 border-gray-500/30',
};

const VigieImmobilierePage = () => {
  const { api, user } = useAuth();
  const navigate = useNavigate();
  
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedTag, setSelectedTag] = useState(null);
  const [availableTags, setAvailableTags] = useState([]);
  const [favorites, setFavorites] = useState(new Set());
  const [showFavoritesOnly, setShowFavoritesOnly] = useState(false);
  const [subscribedTags, setSubscribedTags] = useState(new Set());
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  useEffect(() => {
    fetchArticles();
    fetchTags();
    if (user) {
      fetchFavorites();
      fetchAlerts();
    }
  }, [page, selectedTag]);

  // Une lecture compte pour le défi "Veilleur immobilier" après 5s passées sur l'article
  const settleReads = async () => {
    try {
      const { data } = await api.post('/news/settle-reads');
      if (data.reads_counted > 0) {
        toast.success(`${data.reads_counted} lecture${data.reads_counted > 1 ? 's' : ''} comptabilisée${data.reads_counted > 1 ? 's' : ''} pour ton défi`);
      }
    } catch (error) {
      // non bloquant
    }
  };

  useEffect(() => {
    if (!user) return;
    settleReads();
    const onVisible = () => { if (document.visibilityState === 'visible') settleReads(); };
    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('focus', onVisible);
    return () => {
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('focus', onVisible);
    };
  }, [user]);

  const markOpened = (articleId) => {
    api.post(`/news/${articleId}/open`).catch(() => {});
  };

  const fetchArticles = async () => {
    try {
      setLoading(true);
      let url = `/news?page=${page}&limit=20`;
      if (selectedTag) url += `&tag=${selectedTag}`;
      if (searchQuery) url += `&search=${encodeURIComponent(searchQuery)}`;
      
      const response = await api.get(url);
      setArticles(response.data.articles || []);
      setTotalPages(response.data.pages || 1);
    } catch (error) {
      console.error('Error fetching news:', error);
      // Load mock data on error
      setArticles([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchTags = async () => {
    try {
      const response = await api.get('/news/tags');
      setAvailableTags(response.data.tags || []);
    } catch (error) {
      setAvailableTags(['investissement', 'fiscalité', 'marché locatif', 'législation', 'crédit', 'construction', 'marché']);
    }
  };

  const fetchFavorites = async () => {
    try {
      const response = await api.get(`/news/favorites/${user.id}`);
      const favIds = new Set(response.data.favorites?.map(f => f.article_id) || []);
      setFavorites(favIds);
    } catch (error) {
      console.error('Error fetching favorites:', error);
    }
  };

  const fetchAlerts = async () => {
    try {
      const response = await api.get(`/news/alerts/${user.id}`);
      setSubscribedTags(new Set(response.data.tags || []));
    } catch (error) {
      console.error('Error fetching alerts:', error);
    }
  };

  const toggleTagAlert = async (tag) => {
    try {
      const response = await api.post(`/news/alerts/${user.id}/toggle-tag?tag=${encodeURIComponent(tag)}`);
      setSubscribedTags(new Set(response.data.tags || []));
      
      if (response.data.action === 'added') {
        toast.success(`🔔 Alertes activées pour "${tag}"`);
      } else {
        toast.success(`🔕 Alertes désactivées pour "${tag}"`);
      }
    } catch (error) {
      toast.error('Erreur lors de la mise à jour des alertes');
    }
  };

  const toggleFavorite = async (articleId) => {
    try {
      if (favorites.has(articleId)) {
        await api.delete(`/news/favorites?user_id=${user.id}&article_id=${articleId}`);
        setFavorites(prev => {
          const next = new Set(prev);
          next.delete(articleId);
          return next;
        });
        toast.success('Retiré des favoris');
      } else {
        await api.post(`/news/favorites?user_id=${user.id}&article_id=${articleId}`);
        setFavorites(prev => new Set(prev).add(articleId));
        toast.success('Ajouté aux favoris');
      }
    } catch (error) {
      toast.error('Erreur lors de la mise à jour');
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    setPage(1);
    fetchArticles();
  };

  const refreshNews = async () => {
    try {
      setLoading(true);
      await api.post('/news/scrape');
      await fetchArticles();
      toast.success('Actualités mises à jour !');
    } catch (error) {
      toast.error('Erreur lors de la mise à jour');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('fr-FR', { 
      day: 'numeric', 
      month: 'short',
      year: 'numeric'
    });
  };

  const displayedArticles = showFavoritesOnly 
    ? articles.filter(a => favorites.has(a.article_id))
    : articles;

  return (
    <div className="min-h-screen bg-background pb-20" data-testid="vigie-page">
      <div className="noise-overlay" />

      {/* Header */}
      <header className="border-b border-border bg-card/50 backdrop-blur-xl sticky top-0 z-10">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate('/')}>
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div className="flex items-center gap-3 flex-1">
            <div className="w-10 h-10 rounded-xl bg-primary/20 flex items-center justify-center">
              <Telescope className="w-5 h-5 text-primary" />
            </div>
            <div>
              <h1 className="font-heading font-bold text-xl">La Vigie Immobilière</h1>
              <p className="text-sm text-muted-foreground">Toute l'actu immo en un coup d'œil</p>
            </div>
          </div>
          <Button variant="outline" size="icon" onClick={refreshNews} disabled={loading}>
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          </Button>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 py-6 space-y-6">
        {/* Search & Filters */}
        <Card className="bg-card/50 border-border">
          <CardContent className="p-4 space-y-4">
            <form onSubmit={handleSearch} className="flex gap-2">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  placeholder="Rechercher par mot-clé..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10 bg-secondary/50"
                  data-testid="search-input"
                />
              </div>
              <Button type="submit">
                <Search className="w-4 h-4" />
              </Button>
            </form>

            {/* Tags filter */}
            <div className="flex flex-wrap gap-2">
              <Button
                variant={selectedTag === null ? "default" : "outline"}
                size="sm"
                onClick={() => { setSelectedTag(null); setPage(1); }}
              >
                Tous
              </Button>
              {availableTags.slice(0, 8).map(tag => (
                <div key={tag} className="flex items-center gap-0.5">
                  <Button
                    variant={selectedTag === tag ? "default" : "outline"}
                    size="sm"
                    onClick={() => { setSelectedTag(tag); setPage(1); }}
                    className="capitalize rounded-r-none"
                  >
                    {tag}
                  </Button>
                  <Button
                    variant={subscribedTags.has(tag) ? "default" : "outline"}
                    size="sm"
                    onClick={() => toggleTagAlert(tag)}
                    className={`rounded-l-none px-1.5 ${subscribedTags.has(tag) ? 'bg-primary' : ''}`}
                    title={subscribedTags.has(tag) ? `Alertes activées pour "${tag}"` : `Activer les alertes pour "${tag}"`}
                  >
                    {subscribedTags.has(tag) ? (
                      <Bell className="w-3 h-3" />
                    ) : (
                      <BellOff className="w-3 h-3 opacity-50" />
                    )}
                  </Button>
                </div>
              ))}
            </div>

            {/* Info about alerts */}
            {subscribedTags.size > 0 && (
              <div className="text-xs text-muted-foreground flex items-center gap-1">
                <Bell className="w-3 h-3 text-primary" />
                Tu recevras une notification pour : {Array.from(subscribedTags).join(', ')}
              </div>
            )}

            {/* Favorites toggle */}
            <div className="flex items-center gap-2">
              <Button
                variant={showFavoritesOnly ? "default" : "outline"}
                size="sm"
                onClick={() => setShowFavoritesOnly(!showFavoritesOnly)}
              >
                <Heart className={`w-4 h-4 mr-1 ${showFavoritesOnly ? 'fill-current' : ''}`} />
                Mes favoris ({favorites.size})
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Articles List */}
        <div className="space-y-4">
          {loading ? (
            // Loading skeletons
            [...Array(5)].map((_, i) => (
              <Card key={i} className="bg-card/50 border-border animate-pulse">
                <CardContent className="p-4">
                  <div className="flex gap-4">
                    <div className="w-24 h-20 bg-secondary rounded-lg" />
                    <div className="flex-1 space-y-2">
                      <div className="h-5 bg-secondary rounded w-3/4" />
                      <div className="h-4 bg-secondary rounded w-1/4" />
                      <div className="h-4 bg-secondary rounded w-full" />
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))
          ) : displayedArticles.length === 0 ? (
            <Card className="bg-card/50 border-border">
              <CardContent className="p-8 text-center">
                <Telescope className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
                <h3 className="font-semibold text-lg mb-2">Aucune actualité trouvée</h3>
                <p className="text-muted-foreground">
                  {showFavoritesOnly 
                    ? "Tu n'as pas encore de favoris. Ajoute des articles qui t'intéressent !"
                    : "Essaie de modifier tes filtres ou de rafraîchir les actualités."
                  }
                </p>
              </CardContent>
            </Card>
          ) : (
            <AnimatePresence>
              {displayedArticles.map((article, index) => (
                <motion.div
                  key={article.article_id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.05 }}
                >
                  <Card className="bg-card/50 border-border hover:border-primary/30 transition-colors group">
                    <CardContent className="p-4">
                      <div className="flex gap-4">
                        {/* Image */}
                        {article.image_url && (
                          <div className="w-24 h-20 flex-shrink-0 rounded-lg overflow-hidden bg-secondary">
                            <img 
                              src={article.image_url} 
                              alt=""
                              className="w-full h-full object-cover"
                              onError={(e) => e.target.style.display = 'none'}
                            />
                          </div>
                        )}

                        {/* Content */}
                        <div className="flex-1 min-w-0">
                          {/* Title */}
                          <a 
                            href={article.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            onClick={() => markOpened(article.article_id)}
                            className="font-semibold text-foreground hover:text-primary transition-colors line-clamp-2 flex items-start gap-2"
                            data-testid={`news-article-link-${article.article_id}`}
                          >
                            {article.title}
                            <ExternalLink className="w-4 h-4 flex-shrink-0 opacity-0 group-hover:opacity-100 transition-opacity" />
                          </a>

                          {/* Source & Date */}
                          <div className="flex items-center gap-2 mt-1 text-xs text-muted-foreground">
                            <Globe className="w-3 h-3" />
                            <span>{article.source}</span>
                            <span>•</span>
                            <Calendar className="w-3 h-3" />
                            <span>{formatDate(article.published_at)}</span>
                          </div>

                          {/* Summary (masqué quand il ne fait que répéter le titre) */}
                          {article.summary && article.summary.trim() !== article.title.trim() && (
                            <p className="text-sm text-muted-foreground mt-2 line-clamp-2">
                              {article.summary}
                            </p>
                          )}
                          {article.summary_ai_generated && (
                            <div className="mt-1.5">
                              <AIGeneratedBadge label={article.summary_translated ? 'Traduit par IA' : 'Résumé IA'} />
                            </div>
                          )}

                          {/* Tags & Favorite */}
                          <div className="flex items-center justify-between mt-3">
                            <div className="flex flex-wrap gap-1">
                              {article.tags?.slice(0, 3).map(tag => (
                                <Badge 
                                  key={tag} 
                                  variant="outline"
                                  className={`text-xs capitalize ${TAG_COLORS[tag] || TAG_COLORS['actualité']}`}
                                >
                                  {tag}
                                </Badge>
                              ))}
                            </div>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => toggleFavorite(article.article_id)}
                              className="text-muted-foreground hover:text-primary"
                            >
                              {favorites.has(article.article_id) ? (
                                <Star className="w-4 h-4 fill-primary text-primary" />
                              ) : (
                                <StarOff className="w-4 h-4" />
                              )}
                            </Button>
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </motion.div>
              ))}
            </AnimatePresence>
          )}
        </div>

        {/* Pagination */}
        {totalPages > 1 && !showFavoritesOnly && (
          <div className="flex justify-center gap-2">
            <Button
              variant="outline"
              disabled={page === 1}
              onClick={() => setPage(p => p - 1)}
            >
              Précédent
            </Button>
            <span className="flex items-center px-4 text-sm text-muted-foreground">
              Page {page} sur {totalPages}
            </span>
            <Button
              variant="outline"
              disabled={page >= totalPages}
              onClick={() => setPage(p => p + 1)}
            >
              Suivant
            </Button>
          </div>
        )}
      </main>
    </div>
  );
};

export default VigieImmobilierePage;
